import { Component, Input, OnChanges, OnInit, SimpleChanges } from "@angular/core";
import { ActionData, ActionState } from "../../types/action-center-types";
import { PageStateModel } from "../../types/page-state-model";
import { PageEvent } from "@angular/material/paginator";
import { eroiIds } from "src/app/features/total-value-delivery/types/tvd-types";
import { tvdMapping } from "src/app/features/action-center/types/constants";

@Component({
    selector: "app-actions-grid",
    templateUrl: "./actions-grid.component.html",
    styleUrls: ["./actions-grid.component.scss"],
    standalone: false
})
export class ActionsGridComponent implements OnInit, OnChanges {
  @Input() currPageState: ActionState | undefined;
  @Input() pageData: PageStateModel | undefined;

  // properties that are used to manipulate the data
  dataManipulationOptions = {
    searchString: '',
    opportunityType: 'sales',
    sortSelection: 'amount',
    selectedFilters: {
      tvd: [] as string[],
      solutionCategory: [] as string[],
      actionStatus: [] as string[]
    }
  };

  displayLoader: boolean = true;

  availableData: ActionData[] | undefined; // this data contains the filtered data as per the search string.
  dataToDisplay: ActionData[] | undefined;  // this data represents the data to be displayed
  currPageIndex: number = 0; // represents the index of the pagination

  ngOnInit(): void {
    this.setUpData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['pageData']) {
      this.setUpData();
    }
  }

  setUpData(): void {
    this.displayLoader = true;
    if (this.pageData && !this.pageData.isEmpty()) {
      this.filterData();
    } else {
      this.dataToDisplay = [];
    }
    this.displayLoader = false;
  }

  /**
   * Filter data based on dataManipulationOptions
   */
  filterData(): void {
    // filter the available data as per the search string
    this.availableData = this.filterDataBySearchString(this.dataManipulationOptions.searchString);

    // apply advanced filters
    this.applyAdvancedFilters(this.dataManipulationOptions.selectedFilters);

    // sort the available data
    this.sortData(this.dataManipulationOptions.sortSelection);

    // reset pagination: create a new page event
    this.resetPagination();
  }

  //#region Data filtering and sorting
  /**
   * Filter data by search string
   * @param search 
   * @returns 
   */
  filterDataBySearchString(search: string): ActionData[] {
    if (this.pageData) {
      return this.pageData.actions.filter(action => {
        return action.productName.toLowerCase().includes(search.toLowerCase());
      });
    }
    return [];
  }

  /**
   * Sort data based on the sort selection: order - descending
   * For amount, it maps it on the basis of opportunity type.
   * if tie, sort by product name
   * @param sortSelection
   */
  sortData(sortSelection: string): void {
    if (this.availableData) {
      if (sortSelection === 'amount') {
        this.availableData.sort((a, b) => {
          let comparison = this.mapOpportunityTypeToCostValue(b, this.dataManipulationOptions.opportunityType) - this.mapOpportunityTypeToCostValue(a, this.dataManipulationOptions.opportunityType);
          if (comparison === 0) {
            return a.productName.localeCompare(b.productName);
          }
          return comparison
      });
      } else if (sortSelection === 'difficulty') {
        this.availableData.sort((a, b) => {
          if (b.groupedDifficultyScale === a.groupedDifficultyScale) {
            return a.productName.localeCompare(b.productName);
          }
          return b.groupedDifficultyScale - a.groupedDifficultyScale
        });
      } else if (sortSelection === 'location') {
        
        this.availableData.sort((a, b) => {
          if (b.currentLocations.length === a.currentLocations.length) {
            return a.productName.localeCompare(b.productName);
          }
          return b.currentLocations.length - a.currentLocations.length;
        });
      }
    }
  }

  /**
   * Apply advanced filters using TVD, Solution Category and Action Status
   */
  applyAdvancedFilters(advancedFilters: { tvd: string[], solutionCategory: string[], actionStatus: string[] }): void {
    if (this.pageData) {
      this.availableData = this.pageData.actions.filter(action => {
        let tvdFilter = advancedFilters.tvd.length === 0 ||
                        advancedFilters.tvd.some(tvdFilterValue => {
                            if (tvdFilterValue === "Assets") {
                                // Compute the sum for Assets
                                const assetsSum = (action.groupedTVDOpportunity.totalRepairDollars || 0) + 
                                                  (action.groupedTVDOpportunity.totalReplacementDollars || 0);
                                return assetsSum > 0;
                            }
                            const mappedKey = tvdMapping[tvdFilterValue];
                            return mappedKey && action.groupedTVDOpportunity[mappedKey as keyof typeof action.groupedTVDOpportunity] > 0;
                        });
        
        let solutionCategoryFilter = advancedFilters.solutionCategory.length === 0 || 
                                     advancedFilters.solutionCategory.map(category => category.toUpperCase()).includes(action.solution);
        
        let actionStatusFilter = advancedFilters.actionStatus.length === 0 || 
                                 advancedFilters.actionStatus.includes(action.recState.recommendationStatus as string);
        
        return tvdFilter && solutionCategoryFilter && actionStatusFilter;
      });
    }
  }
  //#endregion

  /**
   * Reset pagination to default values
   */
  resetPagination(): void {
    if (this.pageData && !this.pageData.isEmpty()) {
      this.currPageIndex = 0;
      const event = new PageEvent();
      event.pageIndex = 0;
      event.pageSize = 25;
      event.length = this.pageData.actions.length;
      this.updateDisplayedData(event);
    }
  }

  /**
   * Update the data to be displayed based on the page event
   * @param event 
   */
  private updateDisplayedData(event: PageEvent) {
    if (this.availableData) {
      this.dataToDisplay = this.availableData.slice(event.pageIndex * event.pageSize, (event.pageIndex + 1) * event.pageSize);
    }
  }

  /**
   * Triggered when the page is changed
   * @param event 
   */
  pageChanged(event: PageEvent) {
    this.currPageIndex = event.pageIndex;
    this.updateDisplayedData(event);
  }

  setOpportunityType(event: any) {
    this.dataManipulationOptions.opportunityType = event;
    this.filterData();
  }

  sortBySelection(event: string) {
    this.dataManipulationOptions.sortSelection = event;
    this.filterData();
  }

  searchData(event: string) {
    this.dataManipulationOptions.searchString = event;
    this.filterData();
  }

  clearSearch() {
    this.searchData("");
  }

  setFilter(event: any) {
    this.dataManipulationOptions.selectedFilters = event;
    this.filterData();
  }

  mapOpportunityTypeToCostValue(row: ActionData, opportunityType: string): number {
    if (row === undefined) {
      return 0;
    }
    switch (opportunityType) {
      case 'sales':
        return row.groupedSalesOpportunity;
      case eroiIds.PRODUCTIVITY:
        return row.groupedTVDOpportunity.totalProductivityDollars;
      case eroiIds.ASSETS:
        return (row.groupedTVDOpportunity.totalReplacementDollars + row.groupedTVDOpportunity.totalRepairDollars);
      case eroiIds.EMPLOYEESAFETY:
        return 0;
      case eroiIds.WATER:
        return row.groupedTVDOpportunity.totalWaterDollars;
      case eroiIds.ENERGY:
        return row.groupedTVDOpportunity.totalEnergyDollars;
      case eroiIds.GREENHOUSEGASES:
        return 0;
      case eroiIds.PLASTIC:
        return row.groupedTVDOpportunity.totalPlasticWasteDollars;
      case eroiIds.CARDBOARD:
        return row.groupedTVDOpportunity.totalCardboardWasteDollars;
      case eroiIds.COSTAVOIDANCE:
        return row.groupedTVDOpportunity.totalCostAvoidance;
      default:
        return 0;
    }
  }
}